import {
  ProductCard,
  ProductCardButton,
  ProductGroup,
  ProductStatusBar,
  ProductStatusLine,
  ProductStatusStep,
  ProductStatusTag
} from './homeProducts.types';

/**
 * Defensive parsers for the `GET /pages/home/products` payload.
 *
 * The endpoint is server-driven and forward-compatible: the client ignores unknown fields, keeps
 * server order, and **drops a malformed group/card/step rather than failing the whole feed** (mirrors
 * the mobile `parseObjectsList` behavior). Unknown `style` values are left as-is here and resolved to a
 * neutral tone at render time.
 *
 * The primitives and the **slot** parsers below are exported because the slot vocabulary
 * (`statusTag`/`statusLine`/`statusBar`/`button`) is shared across surfaces — see the epic's naming rule:
 * `Home*`/`Program*` entities are surface-owned, unprefixed slots are shared. The program feed reuses them
 * from `services/programProducts/programProducts.mappers`; only the card/group shapes are per-surface.
 */

export const asRecord = (value: unknown): Record<string, unknown> | undefined =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;

export const asString = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined;

const asFiniteNumber = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined;

const asArray = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);

/** Maps a raw list to parsed entities, dropping anything the parser rejects. */
export const parseList = <T>(
  value: unknown,
  parse: (raw: Record<string, unknown>) => T | undefined
): T[] =>
  asArray(value).reduce<T[]>((acc, raw) => {
    const record = asRecord(raw);
    const parsed = record && parse(record);
    if (parsed) acc.push(parsed);
    return acc;
  }, []);

export const parseStatusTag = (json: Record<string, unknown>): ProductStatusTag | undefined => {
  const label = asString(json.label);
  if (label === undefined) return undefined;
  return { label, style: asString(json.style) ?? 'regular' };
};

export const parseStatusLine = (json: Record<string, unknown>): ProductStatusLine | undefined => {
  const label = asString(json.label);
  if (label === undefined) return undefined;
  return { label, link: asString(json.link), style: asString(json.style) ?? 'regular' };
};

const parseStep = (json: Record<string, unknown>): ProductStatusStep | undefined => {
  const label = asString(json.label);
  if (label === undefined) return undefined;
  return { label };
};

export const parseStatusBar = (json: Record<string, unknown>): ProductStatusBar => ({
  progress: asFiniteNumber(json.progress),
  steps: parseList(json.steps, parseStep),
  style: asString(json.style) ?? 'info'
});

export const parseButton = (json: Record<string, unknown>): ProductCardButton | undefined => {
  const label = asString(json.label);
  const link = asString(json.link);
  if (label === undefined || link === undefined) return undefined;
  return { label, link };
};

export const parseOptional = <T>(
  value: unknown,
  parse: (raw: Record<string, unknown>) => T | undefined
): T | undefined => {
  const record = asRecord(value);
  return record ? parse(record) : undefined;
};

const parseCard = (json: Record<string, unknown>): ProductCard | undefined => {
  const id = asString(json.id);
  const title = asString(json.title);
  if (id === undefined || title === undefined) return undefined;

  return {
    button: parseOptional(json.button, parseButton),
    id,
    image: asString(json.image),
    link: asString(json.link),
    statusBar: parseOptional(json.statusBar, parseStatusBar),
    statusLine: parseOptional(json.statusLine, parseStatusLine),
    statusTag: parseOptional(json.statusTag, parseStatusTag),
    subtitle: asString(json.subtitle),
    title
  };
};

const parseGroup = (json: Record<string, unknown>): ProductGroup | undefined => {
  const id = asString(json.id);
  const label = asString(json.label);
  if (id === undefined || label === undefined) return undefined;

  return {
    id,
    items: parseList(json.items, parseCard),
    label,
    statusLine: parseOptional(json.statusLine, parseStatusLine)
  };
};

/**
 * Normalizes the raw `groups` payload into ordered, well-formed {@link ProductGroup}s.
 * Any malformed group/card/step is silently dropped; server order is preserved.
 */
export const normalizeGroups = (groups: unknown): ProductGroup[] => parseList(groups, parseGroup);
